import { useEffect, useEffectEvent } from "react";
import { getSubscriptionClient } from "../lib/hyperliquid";

type SubscriptionHandle = {
	unsubscribe: () => Promise<void>;
};

type UseHyperliquidSubscriptionOptions = {
	deps: readonly unknown[];
	enabled?: boolean;
	onBeforeSubscribe?: () => void;
	onError?: (error: unknown) => void;
	subscribe: (
		client: NonNullable<ReturnType<typeof getSubscriptionClient>>,
	) => Promise<SubscriptionHandle>;
};

export function useHyperliquidSubscription({
	deps,
	enabled = true,
	onBeforeSubscribe,
	onError,
	subscribe,
}: UseHyperliquidSubscriptionOptions) {
	const subscribeEvent = useEffectEvent(subscribe);
	const onBeforeSubscribeEvent = useEffectEvent(() => {
		onBeforeSubscribe?.();
	});
	const onErrorEvent = useEffectEvent((error: unknown) => {
		onError?.(error);
	});

	useEffect(() => {
		if (!enabled) return;

		onBeforeSubscribeEvent();

		const client = getSubscriptionClient();
		if (!client) return;

		let cancelled = false;
		let subscription: SubscriptionHandle | null = null;

		void subscribeEvent(client)
			.then((nextSubscription) => {
				if (cancelled) {
					nextSubscription.unsubscribe().catch(() => {});
					return;
				}

				subscription = nextSubscription;
			})
			.catch((error) => {
				if (cancelled) return;
				onErrorEvent(error);
			});

		return () => {
			cancelled = true;
			if (subscription) subscription.unsubscribe().catch(() => {});
		};
	}, [enabled, ...deps]);
}
